import { useCallback } from "react";

import { useTrackImpressionEventByIntersection } from "shared/hooks";
import { getAbsoluteUrl } from "shared/lib/utils";

import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import type { RecirculationWidgetImpressionEvent } from "scmp-app/components/tracking/google-analytics-4/types";

/**
 * Top Stories items are grouped into four sets of four. The v1 homepage uses one
 * `widget_name` per set; v2 keeps the exact same values so GA4 recirculation
 * metrics stay continuous across the v1/v2 AB test.
 */
export const topStoriesSetSize = 4;

const topStoriesSetWidgetNames = [
  "top_story5",
  "top_story8",
  "top_story12",
  "top_story16",
] as const;

const topStoriesSetSequenceBase = [5, 8, 12, 16];

const getTopStoriesSetIndex = (itemIndex: number) =>
  Math.min(Math.floor(itemIndex / topStoriesSetSize), topStoriesSetWidgetNames.length - 1);

type ClickTrackingParameters = {
  entityId?: string;
  index: number;
  isSponsorArticle?: boolean;
  urlAlias?: string;
};

/**
 * Sends the GA4 click event for a Top Stories item, preserving the v1
 * `widget_name` / `article_seq` scheme so the metric is comparable across variants.
 */
export const sendTopStoriesClickTracking = ({
  entityId,
  index,
  isSponsorArticle,
  urlAlias,
}: ClickTrackingParameters) => {
  if (isSponsorArticle) {
    sendGA4Tracking({
      action: "click",
      category: "recirculation",
      customized_parameters: {
        article_id: entityId,
        destination_url: getAbsoluteUrl(urlAlias),
        page_type: "homepage",
        widget_name: "sponsor_article",
      },
      subcategory: "widget",
    });
    return;
  }

  const setIndex = getTopStoriesSetIndex(index);
  sendGA4Tracking({
    action: "click",
    category: "recirculation",
    customized_parameters: {
      article_id: entityId,
      article_seq: `${(index % topStoriesSetSize) + topStoriesSetSequenceBase[setIndex]}`,
      destination_url: getAbsoluteUrl(urlAlias),
      page_type: "homepage",
      widget_name: topStoriesSetWidgetNames[setIndex],
    },
    subcategory: "widget",
  });
};

/**
 * Returns one impression-capture ref per set of four articles. Attaching a ref to
 * the wrapper of each set fires a single `imp` event (with that set's
 * `list_of_articles`) when the set scrolls into view — same behaviour as v1.
 */
export const useTopStoriesImpressionTracking = (listOfArticles: string[]) => {
  const firstImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles.slice(0, 4).join(","),
        page_type: "homepage",
        widget_name: "top_story5",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );
  const {
    captureTrackImpressionEventTargetElement: captureTrackImpressionEventTargetFirstSetElement,
  } = useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
    ga4TrackingHandler: sendGA4Tracking,
    getGa4Event: firstImpressionGA4Event,
    options: { isSendGA4Tracking: true, shouldSendOnce: true },
  });

  const secondImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles.slice(4, 8).join(","),
        page_type: "homepage",
        widget_name: "top_story8",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );
  const {
    captureTrackImpressionEventTargetElement: captureTrackImpressionEventTargetSecondSetElement,
  } = useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
    ga4TrackingHandler: sendGA4Tracking,
    getGa4Event: secondImpressionGA4Event,
    options: { isSendGA4Tracking: true, shouldSendOnce: true },
  });

  const thirdImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles.slice(8, 12).join(","),
        page_type: "homepage",
        widget_name: "top_story12",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );
  const {
    captureTrackImpressionEventTargetElement: captureTrackImpressionEventTargetThirdSetElement,
  } = useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
    ga4TrackingHandler: sendGA4Tracking,
    getGa4Event: thirdImpressionGA4Event,
    options: { isSendGA4Tracking: true, shouldSendOnce: true },
  });

  const fourthImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        list_of_articles: listOfArticles.slice(12).join(","),
        page_type: "homepage",
        widget_name: "top_story16",
      },
      subcategory: "widget",
    }),
    [listOfArticles],
  );
  const {
    captureTrackImpressionEventTargetElement: captureTrackImpressionEventTargetFourthSetElement,
  } = useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
    ga4TrackingHandler: sendGA4Tracking,
    getGa4Event: fourthImpressionGA4Event,
    options: { isSendGA4Tracking: true, shouldSendOnce: true },
  });

  return {
    captureTrackImpressionEventTargetFirstSetElement,
    captureTrackImpressionEventTargetFourthSetElement,
    captureTrackImpressionEventTargetSecondSetElement,
    captureTrackImpressionEventTargetThirdSetElement,
  };
};

/**
 * Returns the impression-capture ref for the native-ad side-card. Keeps the v1
 * `sponsor_article` `widget_name` so the sponsored-article metric stays
 * comparable across the v1/v2 AB test.
 */
export const useTopStoriesNativeAdImpressionTracking = () => {
  const nativeAdImpressionGA4Event = useCallback<() => RecirculationWidgetImpressionEvent>(
    () => ({
      action: "imp",
      category: "recirculation",
      customized_parameters: {
        page_type: "homepage",
        widget_name: "sponsor_article",
      },
      subcategory: "widget",
    }),
    [],
  );
  const { captureTrackImpressionEventTargetElement } =
    useTrackImpressionEventByIntersection<RecirculationWidgetImpressionEvent>({
      ga4TrackingHandler: sendGA4Tracking,
      getGa4Event: nativeAdImpressionGA4Event,
      options: { isSendGA4Tracking: true, shouldSendOnce: true },
    });
  return captureTrackImpressionEventTargetElement;
};
